import BodyText from 'LuxKit/Typography/BodyText'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import LineCopyIcon from 'LuxKit/Icons/line/LineCopyIcon'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import Group from 'components/utils/Group'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import useCopyWithFeedback from 'hooks/useCopyWithFeedback'
import { tierNormalBadgeStyle } from 'styles/utils/tierStyles'

const CopiedFeedback = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  line-height: 1;
`

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${rem(8)};
  padding: ${rem(16)};
  flex: 1 1 ${rem(240)};
  min-width: 0;
  min-height: 0;
`

const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${rem(12)};
`

const CardHeaderLeft = styled(Group)`
  min-width: 0;
`

const ViewOrderLink = styled.button`
  all: unset;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${rem(2)};
  color: ${props => props.theme.palette.brand.primary.normal};
  font-size: ${rem(12)};
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`

const PromoCodeItem = styled(ClickableListItem)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.five};
`

/** Matches no-code variation message: grey background (neutral.seven), no border-radius. */
const AvailableLaterBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
`

const AvailableLaterIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const GeneratingBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${rem(8)};
  padding: ${rem(12)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${rem(6)};
`

const CardTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${rem(8)};
  min-width: 0;
`

const DataBadge = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-top: ${rem(1)};
  padding: ${rem(2)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.S};
  font-size: ${rem(11)};
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  ${tierNormalBadgeStyle}
`

interface Props {
  destination: string
  dates: string
  phase: App.LoyaltyEsimCodePhase
  code?: string
  availableDate?: string
  orderId?: string
  onViewOrder?: (orderId: string) => void
  tier?: App.LoyaltyTier
  dataAllowanceGb?: number
}

function LoyaltyEsimDetailsPromoCodeCard(props: Props) {
  const { destination, dates, phase, code, availableDate, orderId, onViewOrder, tier, dataAllowanceGb } = props
  const intl = useIntl()
  const { isCopied: showCopiedTooltip, copyText: copyToClipboard } = useCopyWithFeedback()

  const handleCopyClick = useCallback(() => {
    if (!code) return
    copyToClipboard(code)
  }, [code, copyToClipboard])

  const handleViewOrder = useCallback(() => {
    if (orderId && onViewOrder) onViewOrder(orderId)
  }, [orderId, onViewOrder])

  const copiedFeedbackIcon = (
    <CopiedFeedback>
      <BodyText variant="small" colour="neutral-three">
        {intl.formatMessage({
          id: '_yk6U9B2jYDeO5WBmcSv',
          defaultMessage: 'Copied',
          description: 'Text shown beside tick icon after copying eSIM promo code',
        })}
      </BodyText>
      <LineCheckIcon size="S" />
    </CopiedFeedback>
  )

  return (
    <CardContainer>
      <CardHeaderRow>
        <CardHeaderLeft direction="vertical" gap={4}>
          <CardTitleRow>
            <BodyText variant="medium" weight="bold">{destination}</BodyText>
            {tier && !!dataAllowanceGb && <DataBadge className={`tier-${tier}`}>
              {intl.formatMessage({
                id: 'kbOjGIMq5fpuQI9S1nHJ',
                defaultMessage: '{amount} GB',
                description: 'eSIM data allowance per tier in benefits table (e.g. 1 GB, 3 GB)',
              }, { amount: dataAllowanceGb })}
            </DataBadge>}
          </CardTitleRow>
          <BodyText variant="medium">{dates}</BodyText>
        </CardHeaderLeft>
        {orderId && onViewOrder && <ViewOrderLink onClick={handleViewOrder}>
          {intl.formatMessage({
            id: 'NQrocXzL8j1WbaN9B8Rx',
            defaultMessage: 'View order',
            description: 'Link text on eSIM promo card to open this booking in My Escapes',
          })}
          <LineAngleRightIcon size="XS" />
        </ViewOrderLink>}
      </CardHeaderRow>

      {phase === 'loading' && <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />}

      {phase === 'generating' && <GeneratingBox>
        <LoadingBox height={44} borderRadius="S" style={{ width: '100%' }} />
        <BodyText variant="small" colour="neutral-three">
          {intl.formatMessage({
            id: 'VwWj-NPVDEGK-lTnHgwU',
            defaultMessage: 'Generating your code, this may take up to 5 minutes…',
            description: 'Message shown while eSIM promo code is being generated',
          })}
        </BodyText>
      </GeneratingBox>}

      {phase === 'success' && !!code && <PromoCodeItem
        title={code}
        titleLineClamp={1}
        size="medium"
        endIcon={showCopiedTooltip ? copiedFeedbackIcon : <LineCopyIcon size="S" />}
        onClick={handleCopyClick}
      />}

      {phase === 'available_later' && !!availableDate && <AvailableLaterBox>
        <AvailableLaterIcon>
          <LineInfoCircleIcon size="S" />
        </AvailableLaterIcon>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'CiVosmaCVp3MuhwRlIPH',
            defaultMessage: 'Code available on {date}',
            description: 'Message shown when eSIM promo code is not yet available',
          }, { date: availableDate })}
        </BodyText>
      </AvailableLaterBox>}

      {phase === 'error' && <MessageBanner
        kind="critical"
        icon={<LineInfoCircleIcon />}
      >
        {intl.formatMessage({
          id: 'oKIty-ovzL3IH40vIXVP',
          defaultMessage: 'Unable to load promo code. Please try again later.',
          description: 'Error message displayed when the eSIM promo code fails to load',
        })}
      </MessageBanner>}
    </CardContainer>
  )
}

export default LoyaltyEsimDetailsPromoCodeCard
